import type { FormikProps } from 'formik';
import { useCallback } from 'react';

import './CustomerLeadStepAddressTab.scss';

import { SelectField, TextInputField } from 'src/components/base';
import type { ICustomerLeadProductAddressTabFormValues } from 'src/components/customers/steps/Contact/CustomerLeadStepAddressTab.validation';
import { CustomerLeadStepLayoutNextButton } from 'src/components/customers/steps/CustomerLeadStepLayoutNextButton';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
import { CANADA_STATE_OPTIONS, COUNTRY_OPTIONS, Country, USA_STATE_OPTIONS } from 'src/types';

type CustomerLeadStepContactTabProps = {
  formikProps: FormikProps<ICustomerLeadProductAddressTabFormValues>;

  onChange: (newValues: ICustomerLeadProductAddressTabFormValues) => void;
};

export function CustomerLeadStepAddressTabContent({
  formikProps: { values, setValues, validateForm },
  onChange,
}: Readonly<CustomerLeadStepContactTabProps>) {
  const handleCountryChange = useCallback(
    (newCountry: string) => {
      void setValues(
        (prev) => ({
          ...prev,

          address: {
            country: newCountry,

            region: null,
            city: null,
            street: null,
            postalCode: null,
          },
        }),
        false
      );
    },
    [setValues]
  );

  useDebounceEffect(
    () => {
      void (async () => {
        const errors = await validateForm(values);

        if (Object.keys(errors).length === 0) {
          onChange(values);
        }
      })();
    },
    [values],
    1000
  );

  const country = values.address.country;
  const isRegionDisabled = !country;

  return (
    <div className="customer-lead-step-address-tab__container">
      <div className="customer-lead-step-address-tab__container__row">
        <div className="w-100">
          <SelectField
            name="address.country"
            label="Country"
            options={COUNTRY_OPTIONS}
            onChange={handleCountryChange}
          />
        </div>
        <div className="w-100">
          <SelectField
            name="address.region"
            label={country === Country.Canada ? 'Province/Territory' : 'State'}
            options={country === Country.Canada ? CANADA_STATE_OPTIONS : USA_STATE_OPTIONS}
            disabled={isRegionDisabled}
          />
        </div>
      </div>
      <div className="customer-lead-step-address-tab__container__row">
        <div className="w-100">
          <TextInputField name="address.street" label="Street" />
        </div>
        <div className="customer-lead-step-address-tab__container__subrow">
          <div className="w-100">
            <TextInputField name="address.city" label="City" />
          </div>
          <TextInputField
            name="address.postalCode"
            type="zip"
            country={country ?? undefined}
            label={country === Country.Canada ? 'Postal Code' : 'Zip'}
            disabled={isRegionDisabled}
          />
        </div>
      </div>

      <div className="customer-lead-step__layout__center__footer-actions">
        <CustomerLeadStepLayoutNextButton />
      </div>
    </div>
  );
}
